/**
 * Phira's ClampedTween (curve slicing with easingLeft / easingRight, F§6.6, P§1.5) and the slice rules the model
 * and the easing picker need.
 *
 *   y(x) = (f(L + (R - L) x) - f(L)) / (f(R) - f(L))           all in f32, x unclamped
 *
 * There is no guard: when f(R) == f(L) (possible on Back, Elastic and Bounce, or with L == R) the division gives
 * NaN where the numerator is 0 and +-Infinity elsewhere, exactly as Phira computes it.
 */

import { resolveRpe, rpeToTweenId, ZERO_BEZIER } from './rpeMap';
import { staticY } from './tweens';
import type { Ease, TweenId } from './types';

const fr = Math.fround;

/** ClampedTween y(x) for a Phira tween id and slice [l, r] (rounded to f32 like Phira's stored range). */
export function clampedY(id: TweenId, l: number, r: number, x: number): number {
  const lo = fr(l);
  const hi = fr(r);
  const fl = staticY(id, lo);
  const fh = staticY(id, hi);
  const arg = fr(lo + fr(fr(hi - lo) * fr(x)));
  return fr(fr(staticY(id, arg) - fl) / fr(fh - fl));
}

/** True when Phira applies the slice of a MODEL ease (tween id > linear, not (l~0 && r~1), l < r). */
export function sliceApplies(e: Ease): boolean {
  if (e.kind !== 'tween') return false;
  return resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER).kind === 'clamped';
}

/**
 * The slice Phira actually uses for a model tween: the clamped (f32) slice when it applies, else the whole
 * curve [0, 1].
 */
export function effectiveSlice(e: Ease & { readonly kind: 'tween' }): {
  readonly left: number;
  readonly right: number;
  readonly applies: boolean;
} {
  const desc = resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  return desc.kind === 'clamped'
    ? { left: desc.l, right: desc.r, applies: true }
    : { left: 0, right: 1, applies: false };
}

/**
 * True when a slice [l, r] of RPE easingType `easingType` is invalid in Phira: f(l) == f(r) in f32 (the clamped
 * tween divides by zero) or the denominator is not finite. l and r are clamped to [0, 1] and rounded to f32 as
 * Phira reads them. l == r is always degenerate.
 */
export function sliceIsDegenerate(easingType: number, l: number, r: number): boolean {
  const id = rpeToTweenId(easingType);
  const lo = clamp01(fr(l));
  const hi = clamp01(fr(r));
  const den = fr(staticY(id, hi) - staticY(id, lo));
  return !Number.isFinite(den) || den === 0;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
